<template>
  <div class="container">
    <h2 class="">
      <i18n path="common.install" />
    </h2>

    <p><i18n path="common.guide.useNpm" /></p>
    <showcode :trigger="false">
      <pre class="m-0">npm i -S v-selectpage</pre>
    </showcode>

    <p><i18n path="common.guide.include" /></p>
    <showcode :trigger="false">
      <v-code lang="javascript">
        <pre>
        // use v-selectpage as a plugin
        import Vue from 'vue'
        import vSelectPage from 'v-selectpage'
        Vue.use(vSelectPage, {
          // server side data loader
          dataLoad: function(vue, data, params){
            return new Promise((resolve, reject)=>{
              axios.post(url, params).then(resp => resolve(resp), resp => reject(resp))
            })
          }
        })

        // use v-selectpage as a component
        import Vue from 'vue'
        import { SelectPage } from 'v-selectpage'
        // component
        export default {
          components: {
            'v-selectpage': SelectPage
          }
        }
        </pre>
      </v-code>
    </showcode>

    <div class="card p-4">
      <p><i18n path="common.globalConfig" /></p>
      <ul>
        <li>title</li>
        <li>language</li>
        <li>placeholder</li>
        <li>pageSize</li>
        <li>dataLoad</li>
        <li>rtl</li>
      </ul>
    </div>

    <h2 class="mt-5">
      <i18n path="common.deploy" />
    </h2>

    <showcode :trigger="false">
      <v-code>
        <pre>
        &lt;template&gt;
          &lt;v-selectpage :data="list" key-field="id" show-field="name" &gt;
          &lt;/v-selectpage&gt;
        &lt;/template&gt;

        &lt;script&gt;
        export default {
          data () {
            return {
              list: [
                { id:1, name: 'Chicago Bulls', desc: '芝加哥公牛' },
                { id:2, name: 'Cleveland Cavaliers', desc: '克里夫兰骑士' },
                { ... }
              ]
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h2 class="mt-5">
      <i18n path="common.example" />
      <!--      <small class="text-muted ms-2"><i18n path="selectpage.demo.desc"></i18n></small>-->
      <small class="ms-2">
        <a
          href="https://codepen.io/terry05/pen/wXNKOK"
          target="_blank"
        >
          <i18n path="common.codePen" />
        </a>
      </small>
    </h2>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.single" />
    </h4>
    <p><i18n path="common.selectedResults" /></p>
    <pre class="p-3 bg-light">{{ JSON.stringify(singleValue, null, 2) }}</pre>
    <p><i18n path="selectpage.demo.valuesEvent" /></p>
    <pre class="p-3 bg-light">{{ JSON.stringify(singleValueObj, null, 2) }}</pre>
    <p>
      <v-selectpage
        :data="sample"
        v-model="singleValue"
        sort="name desc"
        @values="singleValues"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-selectpage
          :data="sample"
          v-model="singleValue"
          sort="name desc"
          @values="singleValues"
        &gt;
        &lt;/v-selectpage&gt;

        &lt;script&gt;
        export default {
          data () {
            return {
              singleValue: '7'
            };
          },
          methods: {
            singleValues (data) {
              // console.log(data);
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.custom" />
    </h4>
    <p>
      <button
        type="button"
        class="btn btn-danger me-2"
        @click="disabled=true"
      >
        <i18n path="selectpage.demo.btnDisabled" />
      </button>
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="disabled=false"
      >
        <i18n path="selectpage.demo.btnEnabled" />
      </button>
    </p>
    <p>
      <v-selectpage
        :data="sample"
        :show-field="renderRow"
        :disabled="disabled"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;!-- control buttons --&gt;
        &lt;button type="button" @click="disabled=true"&gt;Disabled&lt;/button&gt;
        &lt;button type="button" @click="disabled=false"&gt;Enabled&lt;/button&gt;

        &lt;v-selectpage :data="sample" :show-field="renderRow" :disabled="disabled"&gt;
        &lt;/v-selectpage&gt;

        &lt;script&gt;
        export default {
          data () {
            return {
              disabled: false,
              renderRow (row) {
                let result = ''
                if(row && Object.keys(row).length)
                  result = `[&lt;b&gt;${row.id&lt;10?'0'+row.id:row.id}&lt;/b&gt;] - ${row.name} (${row.desc})`
                return result
              }
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.nopage" />
    </h4>
    <p>
      <v-selectpage
        :data="sample"
        :pagination="false"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-selectpage :data="sample" :pagination="false" &gt;
        &lt;/v-selectpage&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.modify" />
    </h4>
    <p>
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="selected='22'"
      >
        <i18n path="selectpage.demo.btnModify" />
      </button>
    </p>
    <p>
      <v-selectpage
        :data="sample"
        v-model="selected"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;button type="button" @click="selected='22'"&gt;Modify&lt;/button&gt;

        &lt;!-- init by {selected: '7'} --&gt;
        &lt;v-selectpage :data="sample" v-model="selected" &gt;&lt;/v-selectpage&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.multiple" />
    </h4>
    <p><i18n path="common.selectedResults" /></p>
    <pre class="p-3 bg-light">{{ JSON.stringify(multipleValue, null, 4) }}</pre>
    <p><i18n path="selectpage.demo.valuesEvent" /></p>
    <pre class="p-3 bg-light">{{ JSON.stringify(multipleValueObj, null, 4) }}</pre>
    <p>
      <button
        type="button"
        class="btn btn-outline-secondary"
        @click="tmpData=sample1.concat()"
      >
        <i18n path="selectpage.demo.btnChange" />
      </button>
    </p>

    <p>
      <v-selectpage
        :data="tmpData"
        :multiple="true"
        :max-select-limit="3"
        v-model="multipleValue"
        @values="multipleValues"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;button type="button" @click="tmpData=sample1.concat()"&gt;Change data source&lt;/button&gt;

        &lt;v-selectpage
          :data="tmpData"
          :multiple="true"
          :max-select-limit="3"
          v-model="multipleValue"
          @values="multipleValues"
        &gt;
        &lt;/v-selectpage&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.tbSingle" />
    </h4>
    <p>
      <v-selectpage
        :data="sample1"
        :tb-columns="showFields"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-selectpage :data="sample1" :tb-columns="columns"&gt;
        &lt;/v-selectpage&gt;

        &lt;script&gt;
        export default {
          data(){
            return {
              sample1: [
                {id:1 ,name:'The People\'s Republic of China',desc:'中华人民共和国'},
                {id:2 ,name:'United States of America',desc:'美利坚合众国'},
                {...}
              ],
              columns:[
                {title: 'id',data: 'id'},
                {title: 'name',data: 'name'},
                {title: 'desc',data: 'desc'},
              ]
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.tbMultiple" />
    </h4>
    <p>
      <v-selectpage
        :data="sample1"
        :multiple="true"
        :tb-columns="showFields"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-selectpage :data="sample1" :multiple="true" :tb-columns="columns"&gt;
        &lt;/v-selectpage&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.server" />
    </h4>
    <p>
      <i18n path="selectpage.demo.serverResponse" />:
      <a
        href="http://www.mocky.io/v2/5b3777a72e0000570036c7a5"
        target="_blank"
      >Server Response</a>
    </p>
    <p>
      <v-selectpage
        data="www.mocky.io/v2/5b3777a72e0000570036c7a5"
        key-field="id"
        show-field="name"
        search-field="userName"
        v-model="server"
        :result-format="resultFormat"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;v-selectpage
          data="yourserver/load"
          key-field="id"
          show-field="name"
          search-field="userName"
          v-model="initValue"
          :result-format="resultFormat"&gt;
        &lt;/v-selectpage&gt;

        &lt;script&gt;
        export default {
          data(){
            return {
              sample1: [
                {id:1 ,name:'The People\'s Republic of China',desc:'中华人民共和国'},
                {id:2 ,name:'United States of America',desc:'美利坚合众国'},
                {...}
              ],
              showFields:[
                {title: 'id',data: 'id'},
                {title: 'name',data: 'name'},
                {title: 'desc',data: 'desc'},
              ]
            }
          },
          methods: {
            resultFormat(resp){
              if(resp && resp.data) return resp.data.values.gridResult
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.rtl" />
    </h4>
    <p>
      <v-selectpage
        :data="sample"
        :rtl="true"
      />
    </p>
    <p>
      <v-selectpage
        :data="sample1"
        :tb-columns="showFields"
        :rtl="true"
      />
    </p>
    <showcode>
      <v-code>
        <pre>
        &lt;!-- list view  --&gt;
        &lt;v-selectpage :data="sample" :rtl="true"&gt;
        &lt;/v-selectpage&gt;
        &lt;!-- table view  --&gt;
        &lt;v-selectpage :data="sample1" :tb-columns="showFields" :rtl="true"&gt;
        &lt;/v-selectpage&gt;

        &lt;script&gt;
        export default {
          data(){
            return {
              sample: [
                {id:1 ,name:'Chicago Bulls',desc:'芝加哥公牛'},
                {id:2 ,name:'Cleveland Cavaliers',desc:'克里夫兰骑士'},
                {...}
              ],
              sample1: [
                {id:1 ,name:'The People\'s Republic of China',desc:'中华人民共和国'},
                {id:2 ,name:'United States of America',desc:'美利坚合众国'},
                {...}
              ],
              showFields:[
                {title: 'id',data: 'id'},
                {title: 'name',data: 'name'},
                {title: 'desc',data: 'desc'},
              ]
            }
          }
        }
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>

    <h4 class="mt-5">
      <i18n path="selectpage.demo.useful" />
    </h4>
    <div class="card p-3 shadow-sm">
      <div class="row mt-3">
        <div class="col-md-6">
          <h5><i18n path="selectpage.demo.countries" /></h5>
          <p>
            <v-selectpage
              :data="countries"
              key-field="key"
              show-field="value"
              :tb-columns="countryFields"
              title="Countries of the world"
            />
          </p>
          <p>
            <i18n path="selectpage.demo.datasource" />:
            <a
              href="https://github.com/umpirsky/country-list/blob/master/data/en_US/country.json"
              target="_blank"
            >country-list</a>
            <i18n path="selectpage.demo.format" />
          </p>
          <showcode :trigger="false">
            <v-code lang="javascript">
              <pre>
              Object.entries(country).map(arr => {
                return {
                  key: arr[0],
                  value: arr[1]
                };
              })
              </pre>
            </v-code>
          </showcode>
          <showcode>
            <v-code>
              <pre>
              &lt;v-selectpage
                :data="countries"
                key-field="key"
                show-field="value"
                :tb-columns="countryFields"
                title="Countries of the world"
              &gt;
              &lt;/v-selectpage&gt;

              &lt;script&gt;
              export default {
                data () {
                  return {
                    countryFields: [
                      { title: 'abbr', data: 'key' },
                      { title: 'country', data: 'value' }
                    ]
                  }
                }
              }
              &lt;script&gt;
              </pre>
            </v-code>
          </showcode>
        </div>

        <div class="col-md-6">
          <h5><i18n path="selectpage.demo.timezones" /></h5>
          <p>
            <v-selectpage
              :data="timezones"
              key-field="key"
              show-field="value"
              :tb-columns="timezoneFields"
              title="Countries of the world"
            />
          </p>
          <p>
            <i18n path="selectpage.demo.datasource" />:
            <a
              href="https://gist.github.com/TerryZ/4ddab237f80ef7e27e5eb206d9e66e24"
              target="_blank"
            >timezones</a>
            <i18n path="selectpage.demo.format" />
          </p>
          <showcode :trigger="false">
            <v-code lang="javascript">
              <pre>
              Object.entries(timezones).map(arr => {
                return {
                  key: arr[0],
                  value: arr[1]
                }
              })
              </pre>
            </v-code>
          </showcode>
          <showcode>
            <v-code>
              <pre>
              &lt;v-selectpage
                :data="timezones"
                key-field="key"
                show-field="value"
                :tb-columns="timezoneFields"
                title="World time zone"
              &gt;
              &lt;/v-selectpage&gt;

              &lt;script&gt;
              export default {
                data () {
                  return {
                    timezoneFields: [
                      {title: 'area',data: 'key'},
                      {title: 'time zone',data: 'value'}
                    ]
                  }
                }
              }
              &lt;script&gt;
              </pre>
            </v-code>
          </showcode>
        </div>
      </div>
    </div>

    <h2 class="mt-5">
      <i18n path="common.options" />
    </h2>

    <table class="table table-striped option-table">
      <thead>
        <tr>
          <th><i18n path="common.tbTitle.name" /></th>
          <th><i18n path="common.tbTitle.type" /></th>
          <th><i18n path="common.tbTitle.desc" /></th>
          <th><i18n path="common.tbTitle.required" /></th>
          <th><i18n path="common.tbTitle.default" /></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>v-model</td>
          <td>String</td>
          <td><i18n path="selectpage.option.v-model" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>data</td>
          <td>Array|String</td>
          <td><i18n path="selectpage.option.data" /></td>
          <td>true</td>
          <td />
        </tr>
        <tr>
          <td>key-field</td>
          <td>String</td>
          <td><i18n path="selectpage.option.keyField" /></td>
          <td>false</td>
          <td>id</td>
        </tr>
        <tr>
          <td>show-field</td>
          <td>String|Function</td>
          <td><i18n path="selectpage.option.showField" /></td>
          <td>false</td>
          <td>name</td>
        </tr>
        <tr>
          <td>search-field</td>
          <td>String</td>
          <td><i18n path="selectpage.option.searchField" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>tb-columns</td>
          <td>Array</td>
          <td>
            <i18n path="selectpage.option.tbColumns" />
            <v-code lang="javascript">
              <pre>
              [
                { title: 'id',data: 'id' },
                { title: 'name',data: 'name' },
                { title: 'desc',data: function(row){
                  return row.name + ' ' +row.desc;
                }},
              ]
              </pre>
            </v-code>
          </td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>sort</td>
          <td>String</td>
          <td><i18n path="selectpage.option.sort" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>params</td>
          <td>Object</td>
          <td><i18n path="selectpage.option.params" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>title</td>
          <td>String</td>
          <td><i18n path="selectpage.option.title" /></td>
          <td>false</td>
          <td>SelectPage</td>
        </tr>
        <tr>
          <td>result-format</td>
          <td>Function</td>
          <td>
            <i18n path="selectpage.option.resultFormat" />
            <v-code lang="javascript">
              <pre>
              {
                totalRow: 100,
                list: [
                  { ... },
                  { ... },
                  { ... }
                ]
              }
              </pre>
            </v-code>
          </td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>placeholder</td>
          <td>String</td>
          <td><i18n path="selectpage.option.placeholder" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>multiple</td>
          <td>Boolean</td>
          <td><i18n path="selectpage.option.multiple" /></td>
          <td>false</td>
          <td>false</td>
        </tr>
        <tr>
          <td>language</td>
          <td>String</td>
          <td>
            <i18n path="common.lang.desc" /><br>
            <ul class="mt-3">
              <li><code class="me-2">cn</code><i18n path="common.lang.cn" /></li>
              <li><code class="me-2">en</code><i18n path="common.lang.en" /></li>
              <li><code class="me-2">jp</code><i18n path="common.lang.jp" /></li>
              <li><code class="me-2">ar</code><i18n path="common.lang.ar" /></li>
              <li><code class="me-2">es</code><i18n path="common.lang.es" /></li>
              <li><code class="me-2">de</code><i18n path="common.lang.de" /></li>
              <li><code class="me-2">ro</code><i18n path="common.lang.ro" /></li>
              <li><code class="me-2">fr</code><i18n path="common.lang.fr" /></li>
              <li><code class="me-2">pt_br</code><i18n path="common.lang.pt_br" /></li>
              <li><code class="me-2">pl</code><i18n path="common.lang.pl" /></li>
            </ul>
          </td>
          <td>false</td>
          <td>cn</td>
        </tr>
        <tr>
          <td>page-size</td>
          <td>Number</td>
          <td><i18n path="page.event.pageSize" /></td>
          <td>false</td>
          <td>10</td>
        </tr>
        <tr>
          <td>max-select-limit</td>
          <td>Number</td>
          <td><i18n path="selectpage.option.maxSelectLimit" /></td>
          <td>false</td>
          <td>0</td>
        </tr>
        <tr>
          <td>pagination</td>
          <td>Boolean</td>
          <td><i18n path="selectpage.option.pagination" /></td>
          <td>false</td>
          <td>true</td>
        </tr>
        <tr>
          <td>disabled</td>
          <td>Boolean</td>
          <td><i18n path="selectpage.option.disabled" /></td>
          <td>false</td>
          <td>false</td>
        </tr>
        <tr>
          <td>width</td>
          <td>Number</td>
          <td><i18n path="selectpage.option.width" /></td>
          <td>false</td>
          <td />
        </tr>
        <tr>
          <td>rtl</td>
          <td>Boolean</td>
          <td><i18n path="selectpage.option.rtl" /></td>
          <td>false</td>
          <td>false</td>
        </tr>
      </tbody>
    </table>

    <h2 class="mt-5">
      <i18n path="common.events" />
    </h2>

    <table class="table table-striped">
      <thead>
        <tr>
          <th><i18n path="common.tbTitle.param" /></th>
          <th><i18n path="common.tbTitle.desc" /></th>
          <th><i18n path="common.tbTitle.args" /></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>values</td>
          <td><i18n path="selectpage.event.values" /></td>
          <td>Array</td>
        </tr>
        <tr>
          <td>removed</td>
          <td><i18n path="selectpage.event.removed" /></td>
          <td>Array</td>
        </tr>
      </tbody>
    </table>

    <h2 class="mt-5">
      API
    </h2>

    <p><i18n path="selectpage.api.desc" /></p>
    <showcode :trigger="false">
      <v-code lang="javascript">
        <pre>
        &lt;v-selectpage :data="data" ref="sp" &gt;

        &lt;script&gt;
        //call api
        this.$refs.sp.remove()
        &lt;/script&gt;
        </pre>
      </v-code>
    </showcode>
    <table class="table table-striped">
      <thead>
        <tr>
          <th><i18n path="common.tbTitle.param" /></th>
          <th><i18n path="common.tbTitle.type" /></th>
          <th><i18n path="common.tbTitle.desc" /></th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>remove</td>
          <td />
          <td><i18n path="selectpage.api.remove" /></td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import country from './country'
import timezones from './timezones'
export default {
  data () {
    return {
      btnText: 'please select',
      selectResult: {},

      singleValue: '7',
      singleValueObj: null,
      multipleValue: '3,5,7',
      multipleValueObj: null,
      selected: '7',
      server: '1',
      disabled: false,
      renderRow (row) {
        let result = ''
        if (row && Object.keys(row).length) {
          result = `[<b>${row.id < 10 ? '0' + row.id : row.id}</b>] - ${row.name} (${row.desc})`
        }
        return result
      },
      showFields: [
        { title: 'id', data: 'id' },
        { title: 'name', data: 'name' },
        { title: 'desc', data: 'desc' }
      ],
      countryFields: [
        { title: 'abbr', data: 'key' },
        { title: 'country', data: 'value' }
      ],
      timezoneFields: [
        { title: 'area', data: 'key' },
        { title: 'time zone', data: 'value' }
      ],
      tmpData: [],
      countries: country,
      timezones: timezones,
      sample: [
        { id: 1, name: 'Chicago Bulls', desc: '芝加哥公牛' },
        { id: 2, name: 'Cleveland Cavaliers', desc: '克里夫兰骑士' },
        { id: 3, name: 'Detroit Pistons', desc: '底特律活塞' },
        { id: 4, name: 'Indiana Pacers', desc: '印第安纳步行者' },
        { id: 5, name: 'Milwaukee Bucks', desc: '密尔沃基雄鹿' },
        { id: 6, name: 'Brooklyn Nets', desc: '布鲁克林篮网' },
        { id: 7, name: 'Boston Celtics', desc: '波士顿凯尔特人' },
        { id: 8, name: 'New York Knicks', desc: '纽约尼克斯' },
        { id: 9, name: 'Philadelphia 76ers', desc: '费城76人' },
        { id: 10, name: 'Toronto Raptors', desc: '多伦多猛龙' },
        { id: 11, name: 'Atlanta Hawks', desc: '亚特兰大老鹰' },
        { id: 12, name: 'Charlotte Hornets', desc: '夏洛特黄蜂' },
        { id: 13, name: 'Miami Heat', desc: '迈阿密热火' },
        { id: 14, name: 'Orlando Magic', desc: '奥兰多魔术' },
        { id: 15, name: 'Washington Wizards', desc: '华盛顿奇才' },
        { id: 16, name: 'Denver Nuggets', desc: '丹佛掘金' },
        { id: 17, name: 'Minnesota Timberwolves', desc: '明尼苏达森林狼' },
        { id: 18, name: 'Oklahoma City Thunder', desc: '俄克拉荷马雷霆' },
        { id: 19, name: 'Portland Trail Blazers', desc: '波特兰开拓者' },
        { id: 20, name: 'Utah Jazz', desc: '犹他爵士' },
        { id: 21, name: 'Golden State Warriors', desc: '金州勇士' },
        { id: 22, name: 'Los Angeles Clippers', desc: '洛杉矶快船' },
        { id: 23, name: 'Los Angeles Lakers', desc: '洛杉矶湖人' },
        { id: 24, name: 'Phoenix Suns', desc: '菲尼克斯太阳' },
        { id: 25, name: 'Sacramento Kings', desc: '萨克拉门托国王' },
        { id: 26, name: 'Dallas Mavericks', desc: '达拉斯小牛' },
        { id: 27, name: 'Houston Rockets', desc: '休斯顿火箭' },
        { id: 28, name: 'Memphis Grizzlies', desc: '孟菲斯灰熊' },
        { id: 29, name: 'New Orleans Pelicans', desc: '新奥尔良鹈鹕' },
        { id: 30, name: 'San Antonio Spurs', desc: '圣安东尼奥马刺' }
      ],
      sample1: [
        { id: 1, name: 'The People\'s Republic of China', desc: '中华人民共和国' },
        { id: 2, name: 'United States of America', desc: '美利坚合众国' },
        { id: 3, name: 'United Kingdom of Great Britain and Northern Ireland', desc: '大不列颠及北爱尔兰联合王国' },
        { id: 4, name: 'Japan', desc: '日本' },
        { id: 5, name: 'Russian Federation', desc: '俄罗斯联邦' },
        { id: 6, name: 'The Federal Republic of Germany', desc: '德意志联邦共和国' },
        { id: 7, name: 'French Republic', desc: '法兰西共和国' },
        { id: 8, name: 'The Kingdom of Spain', desc: '西班牙王国' },
        { id: 9, name: 'Republic of India', desc: '印度共和国' },
        { id: 10, name: 'Repubblica Italiana', desc: '意大利共和国' },
        { id: 11, name: 'Canada', desc: '加拿大' },
        { id: 12, name: 'The Republic of Argentina', desc: '阿根廷共和国' },
        { id: 13, name: 'The Kingdom of Sweden', desc: '瑞典王国' },
        { id: 14, name: 'The Kingdom of Norway', desc: '挪威王国' },
        { id: 15, name: 'The Czech Republic', desc: '捷克共和国' }
      ]
    }
  },
  methods: {
    regionChange (d) {
      console.log(d)
      this.btnText = (!d.province && !d.city && !d.area && !d.town) ? '请选择'
        : `${this.getValue(d.province)}${this.getValue(d.city)}${this.getValue(d.area)}${this.getValue(d.town)}`.trim()
    },
    getValue (obj) {
      return obj ? obj.value : ''
    },
    selectLogs (data) {
      if (data) this.selectResult = data
    },
    singleValues (val) {
      this.singleValueObj = val
    },
    multipleValues (val) {
      this.multipleValueObj = val
    },
    resultFormat (resp) {
      if (resp && resp.data) {
        const query = JSON.parse(resp.config.data)
        if (Object.keys(query).findIndex(val => val === 'searchKey') !== -1) {
          const list = resp.data.values.gridResult.list
          const row = list.find(val => String(val[query.searchKey]) === query.searchValue)
          return {
            totalRow: resp.data.values.totalRow,
            list: [row]
          }
        } else return resp.data.values.gridResult
      }
    }
  },
  mounted () {
    this.tmpData = this.sample
  }
}
</script>

<style lang="scss" scoped>
.info-box{
  background-color: #F5F5F5;
  border: 0;
  border-radius: 4px;
  padding: 10px;
}
</style>
